import React from "react";
import { Formik, Form, Field } from "formik";
import { Show, ShowDealType } from "@quantizr-engineering/common/types/show";
import { useShowService } from "../../api/showService";
import DateCell from "../../components/DateCell";

interface CreateShowModalProps {
  onClose: () => void;
  isOpen: boolean;
  runID: number;
}

const CreateShowModal: React.FC<CreateShowModalProps> = ({
  isOpen,
  onClose,
  runID,
}) => {
  const { postShow } = useShowService();

  const initialValues: Show = {
    runID: runID,
    performanceDate: null,
    venue: "",
    city: "",
    state: "",
    dealType: "" as ShowDealType,
    merchSoft: 0,
    merchHard: 0,
    deposits: [],
    expenses: [],
    productionBuyouts: [],
    ticketTypes: [],
    ticketBonuses: [],
    files: [],
    commissions: [],
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-2xl h-5/8 overflow-y-auto max-h-screen">
        <h2 className="text-2xl font-bold mb-4">Create New Show</h2>
        <Formik
          initialValues={initialValues}
          
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              setSubmitting(true);
              await postShow({
                ...values,
                profitSharePercent: 0,
                runID: runID as number,
              });
              resetForm();
              onClose();
            } catch (error) {
              console.error("Error creating show:", error);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, values, getFieldProps }) => (
            <Form>
              <div className="grid grid-cols-12 gap-x-8 mx-8">
                <div className="col-span-12">
                  <label className="label" htmlFor="venue">
                    <span className="label-text">Venue</span>
                  </label>
                  <Field
                    type="text"
                    id="venue"
                    name="venue"
                    className="input input-bordered w-full"
                    required
                  />
                </div>
                <div className="col-span-7">
                  <label className="label" htmlFor="date">
                    <span className="label-text">Date</span>
                  </label>
                  <div className="border border-black rounded-lg py-2">
                    <DateCell {...getFieldProps("performanceDate")} />
                  </div>
                </div>
                <div className="col-span-6">
                  <label className="label" htmlFor="city">
                    <span className="label-text">City</span>
                  </label>
                  <Field
                    type="text"
                    id="city"
                    name="city"
                    className="input input-bordered w-full"
                    required
                  />
                </div>
                <div className="col-span-6">
                  <label className="label" htmlFor="state">
                    <span className="label-text">State</span>
                  </label>
                  <Field
                    type="text"
                    id="state"
                    name="state"
                    className="input input-bordered w-full"
                    required
                  />
                </div>
                <div className="col-span-6">
                  <label className="label" htmlFor="dealType">
                    <span className="label-text">Revenue Type</span>
                  </label>
                  <Field
                    as="select"
                    id="dealType"
                    name="dealType"
                    className="select select-bordered w-full"
                    required
                  >
                    <option value="">Select Revenue Type</option>
                    <option value="Flat Fee">Flat Fee</option>
                    <option value="Revenue Share">Revenue Share</option>
                    <option value="Versus">Versus</option>
                  </Field>
                </div>

                <div className="col-span-12 flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn btn-tertiary mr-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="loading loading-spinner"></span>
                    ) : (
                      "Create Show"
                    )}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CreateShowModal;
